import {Button, colors, measureTextWidth, Panel, Spacer, Spinner, Typography, useOnClickOutside} from 'nf-ui'
import React, {FC, useCallback, useEffect, useRef, useState} from 'react'
import {CurrentOrganisation} from '~/components/CurrentOrganisationContext'
import {getGraphQLErrorMessage} from '~/util'
import {
    CellClickedEventArgs,
    DataEditor,
    DataEditorRef,
    EditableGridCell,
    GridCellKind,
    GridColumn,
    GridKeyEventArgs,
    GridMouseEventArgs,
    GridSelection,
    Item,
    SizedGridColumn,
} from '@glideapps/glide-data-grid'
import {useExtraCells} from '@glideapps/glide-data-grid-cells'
import '@glideapps/glide-data-grid/dist/index.css'
import 'react-responsive-carousel/lib/styles/carousel.min.css'
import {orderBy, uniq} from 'lodash'
import {useAlert} from 'react-alert'
import {PanelLayout} from '~/components/PanelLayout'
import {usePanelNavigator} from '~/components/PanelNavigator'
import {PickCategory} from './PickCategory'
import styled from 'styled-components'
import {PageLoading} from '~/components/PageLoading'
import {ManageColumn} from './ManageColumn'
import {Column, Row} from '~/components/Primitives/Layout'
import {useOrganisationContext} from '~/components/OrganisationContext'
import {CellValue, Field, Profile} from './types'
import {useData} from './useData'
import {Calendar} from '~/components/Primitives/InputFields/Calendar'
import {useDatePicker} from '~/components/Primitives/InputFields/useDatePicker'
import {convertDatesToYYYYMMDD, formatDateYYYYMMDD, parseDateYYYYMMDD} from '~/util/date'
import {PickReportsTo} from './PickReportsTo'
import {createOneTimeRunnable} from './OneTimeRunnable'
import {ManagePhoto} from './ManagePhoto'
import {useQueryParam} from 'use-query-params'
import {ModalNavigator} from '~/components/ModalLayout/ModalNavigator'
import {PhotoCroppingModal, PhotoCroppingState} from '../../PhotoCroppingModal'

const DEBUG = 0

const CellEditor = styled.div`
    position: absolute;
    display: none;
`

const CellEditorInput = styled.input`
    border: none;
    &:focus {
        outline: none;
    }
`

const CellEditorSpinnerContainer = styled.div`
    display: flex;
    justify-content: ${(props) => (props.className?.includes('rename-column') ? 'flex-end' : 'center')};
    align-items: center;
    height: 30px;
`

const AddColumnButton = styled.div`
    font-weight: 200;
    font-size: x-large;
    padding: ${(props) =>
        props.className?.includes('add-column')
            ? '10px 7px 10px 6px'
            : props.className?.includes('scrolling-horizontally')
            ? '4px 7px 4px 8px'
            : '4px 8px 4px 8px'};
    margin: 1px 0px 1px
        ${(props) =>
            props.className?.includes('add-column') && props.className?.includes('scrolling-vertically')
                ? '-16px'
                : props.className?.includes('add-column')
                ? '-1px'
                : props.className?.includes('scrolling-vertically') &&
                  props.className?.includes('scrolling-horizontally')
                ? '-16px'
                : props.className?.includes('scrolling-vertically')
                ? '-17px'
                : props.className?.includes('scrolling-horizontally')
                ? '-2px'
                : '-3px'};
    cursor: pointer;
    background-color: #f8f8f8;
    position: absolute;
    width: 13px;
`

const AddRowButton = styled.div`
    font-weight: 200;
    font-size: x-large;
    padding: ${(props) => (props.className?.includes('add-row') ? '10px 8px 9px 9px' : '4px 9px 2px 9px')};
    margin: ${(props) => (props.className?.includes('scrolling-horizontally') ? '-48px' : '-33px')} 0px 0px;
    cursor: pointer;
    background-color: #ffffff;
    position: absolute;
`

export type ManageDataProps = {
    width: number
    height: number
}

export const ManageData: FC<ManageDataProps> = ({width, height}) => {
    const {currentOrganisation} = CurrentOrganisation.useContainer()
    const [refreshing] = useOrganisationContext.useOrganisationRefreshing()

    const data = useData()

    const dataEditorRef = React.useRef<DataEditorRef | null>(null)
    const {customRenderers} = useExtraCells()
    const [columns, setColumns] = useState<SizedGridColumn[]>()
    const [gridSelection, setGridSelection] = useState<GridSelection>()
    const [cellEditorValue, setCellEditorValue] = useState('')
    const [cellEditorHighlight, setCellEditorHighlight] = useState(-1)
    const alert = useAlert()
    const [hover, setHover] = useState<{col: number; row: number}>({col: -1, row: -1})
    const [otherMouseGesture, setOtherMouseGesture] = useState<{buttons: number; type: string} | undefined>()
    const cellRef = useRef<HTMLDivElement>(null)
    const cellEditorInputRef = useRef<HTMLInputElement>(null)
    const {openPanel: openCellMenu, panelProps: cellMenuProps, closePanel: closeCellMenu} = usePanelNavigator(
        'cellMenu',
    )
    const [editing, setEditing] = useState<{
        columnIndex: number
        rowIndex?: number
        originalValue?: CellValue
        item: Item
    }>()
    const [mutating, setMutating] = useState<{mutation: string; col?: number; row?: number} | undefined>()
    const [highlightGridSelection, setHighlightGridSelection] = useState(true)
    const [modal, setModal] = useQueryParam<string | null>('modal')
    const [photoCropping, setPhotoCropping] = useState<PhotoCroppingState>({currentIndex: 0, selection: []})
    const [mouseIsDown, setMouseIsDown] = useState(true)
    const [activePhotoRowOverride, setActivePhotoRowOverride] = useState(-1)
    const photoUploadRef = useRef<HTMLInputElement>(null)

    const datePicker = useDatePicker({
        value: editing?.originalValue?.values[0] ? parseDateYYYYMMDD(editing?.originalValue?.values[0]) : undefined,
        onChange: async (date: Date | undefined) => {
            if (editing) {
                setData(
                    editing.item,
                    {data: date ? formatDateYYYYMMDD(date) : ''},
                    {
                        onValuesUpdated: () => {
                            setEditing(undefined)
                            closeCellMenu()
                            setTimeout(() => setHighlightGridSelection(true), 50)
                            dataEditorRef?.current?.focus()
                            gridSelection && advanceGridSelection(gridSelection, {createNewRowIfNecessary: true})
                        },
                    },
                )
            }
        },
        onAbort: () => {
            setEditing(undefined)
            closeCellMenu()
            setTimeout(() => setHighlightGridSelection(true), 50)
            dataEditorRef?.current?.focus()
        },
        inputRef: cellEditorInputRef,
    })

    const minimumColumnWidth = (field: Field, headerOnly: boolean = false) =>
        Math.min(
            250,
            headerOnly
                ? measureTextWidth(field.name, {fontSize: 14, fontWeight: 600}) + 60
                : Math.max(
                      ...[
                          measureTextWidth(field.name, {fontSize: 14, fontWeight: 600}) + 60,
                          ...data.profiles().map((profile) => {
                              const cellValue = profile.cellValues.find(
                                  (cellValue) => cellValue.columnIndex === field.index,
                              )
                              return (
                                  measureTextWidth(cellValue?.cellState?.displayValue || cellValue?.values?.[0] || '', {
                                      fontSize: 14,
                                      fontWeight: 200,
                                  }) + 20
                              )
                          }),
                          ...(field.availableValues || []).map(
                              (value) => measureTextWidth(value, {fontSize: 16, fontWeight: 300}) + 20,
                          ),
                      ],
                  ),
        )

    const calculateColumns = (fields: Field[]): SizedGridColumn[] => [
        ...(fields
            .filter((field) => field.name)
            .map((field, index) =>
                index === 0
                    ? {
                          title: '',
                          id: '0',
                          hasMenu: false,
                          width: 32,
                          themeOverride: {
                              cellHorizontalPadding: 2,
                              cellVerticalPadding: 2,
                          },
                      }
                    : {
                          title: field.name,
                          id: `${field.index}`,
                          hasMenu: false,
                          width: (columns || [])[index]?.width
                              ? Math.max(minimumColumnWidth(field, true), (columns || [])[index].width)
                              : minimumColumnWidth(field),
                      },
            ) || []),
        {
            title: '',
            id: 'add-column',
            hasMenu: false,
            width: 30,
        },
    ]

    const lastColumnResize = useRef<Date | undefined>()
    const onColumnResize = useCallback(
        (col: GridColumn, newSize: number) => {
            if (!columns) return
            const index = columns.indexOf(col as SizedGridColumn)
            const newCols = [...columns]
            newCols[index] = {
                ...newCols[index],
                width: Math.max(minimumColumnWidth(data.fields()[index], true), newSize),
            }
            setColumns(newCols)
            lastColumnResize.current = new Date()
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [columns],
    )

    const initializeCellEditor = (col: number, row?: number | undefined, initialText?: string, field?: Field) => {
        const element = document.getElementById('activeCellEditor')
        if (!element) return true
        if (initialText !== undefined) {
            setCellEditorValue(initialText)
        }

        if (col < 0) {
            setCellEditorHighlight(-1)
            element.style['display'] = 'none'
            return true
        }
        const bounds = dataEditorRef.current?.getBounds(col, row)
        if (!bounds) {
            setCellEditorHighlight(-1)
            element.style['display'] = 'none'
            return true
        }

        if (row !== undefined && cellEditorHighlight === -1) {
            const values = data.profiles()[row].cellValues[col]?.values || []
            const index = data.fields()[col].availableValues.indexOf(values[0])
            setCellEditorHighlight(index >= 0 ? index : 0)
        }

        if (row === undefined) {
            element.style['top'] = `${bounds.y + 1}px`
            element.style['left'] = `${bounds.x - 246}px`
            element.style['width'] = `${bounds.width}px`
            element.style['height'] = `${bounds.height}px`
        } else {
            element.style['top'] = `${bounds.y + 3}px`
            element.style['left'] = `${bounds.x - 242}px`
            element.style['width'] = `${bounds.width}px`
            element.style['height'] = `${bounds.height}px`
        }
        element.style['display'] = 'inherit'

        const childElement = element.children[0] as HTMLInputElement
        if (!childElement) return true
        if (row === undefined) {
            childElement.style['width'] = `${bounds.width - 8}px`
            childElement.style['height'] = `${bounds.height - 4}px`
        } else {
            childElement.style['width'] = `${bounds.width - 11}px`
            childElement.style['height'] = `${bounds.height - 9}px`
        }
        childElement.focus()
        setTimeout(() => {
            if (field?.type !== 'date' && typeof childElement.setSelectionRange === 'function') {
                childElement.setSelectionRange(0, (initialText || '').length, 'forward')
            }
        }, 1)
        return true
    }

    const displayCellMenu = ([col, row]: Item) => {
        DEBUG && console.log('ManageData: displayCellMenu() [col, row]', [col, row])
        if (
            data.profiles().length === row ||
            data.fields().length === col ||
            (editing && editing.item[0] === col && editing.item[1] === row)
        ) {
            setEditing(undefined)
            return true
        }
        const field = data.fields()[col]
        if (field.type === 'photo') {
            setTimeout(() => setHighlightGridSelection(true), 50)
            return true
        }
        setTimeout(() => setHighlightGridSelection(false), 50)
        if (field.type !== 'category' && field.type !== 'date' && field.name !== 'Reports To') return true

        const profiles = data.profiles()
        setEditing({
            columnIndex: col,
            rowIndex: row,
            item: [col, row],
            originalValue: profiles[row].cellValues.find((cellValue) => cellValue?.columnIndex === field.index),
        })
        openCellMenu('cellMenu', {itemId: 'cellMenu'})
        if (field.type === 'date') {
            datePicker.setInputPosition('year')
            setTimeout(() => datePicker.setInputPosition('year'), 100)
        }
        return false
    }

    const extractColumn = (profiles: Profile[], columnIndex: number): Profile[] =>
        profiles.map((profile) => ({...profile, cellValues: [profile.cellValues[columnIndex]]}))

    const convertFromReportsTo = (profiles: Profile[], columnIndex: number): Profile[] =>
        extractColumn(profiles, columnIndex).map((profile) => {
            const cellValue = profile.cellValues[0]
            return {
                ...profile,
                cellValues: [
                    {
                        ...cellValue,
                        values: cellValue?.cellState?.displayValue ? [cellValue?.cellState?.displayValue] : [],
                    },
                ],
            }
        })
    const convertToReportsTo = (
        names: string[],
        profileIds: string[],
        profiles: Profile[],
        columnIndex: number,
    ): Profile[] =>
        extractColumn(profiles, columnIndex).map((profile) => {
            const cellValue = profile.cellValues[0]
            const nameIndex = names.indexOf(cellValue.values[0])
            return {
                ...profile,
                cellValues: [
                    {
                        ...cellValue,
                        cellState: {...cellValue.cellState, displayValue: cellValue.values[0]},
                        values: nameIndex >= 0 ? [profileIds[nameIndex]] : cellValue.values,
                    },
                ],
            }
        })
    const displayHeaderRename = (col: number) => {
        if (data.fields().length === col) {
            return
        }
        const field = data.fields()[col]
        setEditing({
            columnIndex: col,
            originalValue: {values: [field.name], columnIndex: col, rowIndex: -1},
            item: [col, -1],
        })
        setTimeout(() => setHighlightGridSelection(false), 50)
    }

    const refreshDataEditor = (columnIndexes: number[], rowIndexes?: number[]) => {
        columnIndexes.forEach((columnIndex) => {
            if (rowIndexes) {
                rowIndexes.forEach((rowIndex) => {
                    dataEditorRef.current?.updateCells([{cell: [columnIndex, rowIndex]}])
                })
            } else {
                for (let row = 0; row < data.profiles().length; row++) {
                    dataEditorRef.current?.updateCells([{cell: [columnIndex, row]}])
                }
            }
        })
    }

    const completeHeaderRename = () => {
        if (!editing || editing.rowIndex !== undefined) return
        DEBUG &&
            console.log('ManageData: completeHeaderRename() [editing, cellEditorValue]', [editing, cellEditorValue])
        if (cellEditorValue === '' || cellEditorValue === editing.originalValue?.values?.[0]) {
            setEditing(undefined)
            setTimeout(() => setHighlightGridSelection(true), 50)
            return
        }

        setHover({col: -1, row: hover.row})
        setMutating({mutation: 'rename-column', col: editing.item[0]})

        const onDone = createOneTimeRunnable(() => {
            setMutating(undefined)
            setEditing(undefined)
            setTimeout(() => setHighlightGridSelection(true), 50)
        })
        const onFieldsUpdated = (updatedFields: Field[]) => {
            setColumns(calculateColumns(updatedFields))
            refreshDataEditor([editing.item[0]])
            onDone()
        }
        const oldName = data.fields()[editing.columnIndex].name

        if (cellEditorValue === 'Reports To') {
            data.updateFields([{...data.fields()[editing.columnIndex], name: cellEditorValue}], {
                skipValues: true,
                onFieldsChanged: (updatedFields: Field[]) => {
                    data.updateValues(
                        convertToReportsTo(
                            data.calculateNames(data.fields(), data.profiles()),
                            data.profileIds(),
                            data.profiles(),
                            editing.columnIndex,
                        ),
                        {
                            removeMissingValues: true,
                            onComplete: () => onFieldsUpdated(updatedFields),
                            callerPath: ['completeHeaderRename/onComplete'],
                        },
                    )
                },
                callerPath: ['completeHeaderRename'],
            })
        } else if (oldName === 'Reports To') {
            data.updateFields([{...data.fields()[editing.columnIndex], name: cellEditorValue}], {
                skipValues: true,
                onFieldsChanged: (updatedFields: Field[]) => {
                    data.updateValues(convertFromReportsTo(data.profiles(), editing.columnIndex), {
                        removeMissingValues: true,
                        onComplete: () => onFieldsUpdated(updatedFields),
                        callerPath: ['completeHeaderRename/onComplete'],
                    })
                },
                callerPath: ['completeHeaderRename'],
            })
        } else {
            data.updateFields([{...data.fields()[editing.columnIndex], name: cellEditorValue}], {
                onFieldsChanged: onFieldsUpdated,
                callerPath: ['completeHeaderRename'],
            })
        }
        setMutating(undefined)
    }

    const refreshCellMenu = (field: Field, options?: {hide?: boolean; newValues?: string[]}) => {
        if (options?.hide) {
            setEditing(undefined)
            closeCellMenu()
            setTimeout(() => setHighlightGridSelection(true), 50)
            return
        }
        if (!editing) return

        const [col, row] = editing.item
        if (field.type !== 'category' && field.name !== 'Reports To') return
        const profiles = data.profiles()
        const values =
            options?.newValues ||
            profiles[row].cellValues
                .filter((cellValue) => cellValue.columnIndex === field.index)
                .flatMap((cellValue) => cellValue.values)

        initializeCellEditor(col, row, '', field)
        setEditing({
            columnIndex: col,
            originalValue: {values, columnIndex: col, rowIndex: row},
            rowIndex: row,
            item: [col, row],
        })
        setTimeout(() => setHighlightGridSelection(false), 50)
    }

    useOnClickOutside([cellRef], completeHeaderRename)

    const addCategoryAndApply = (value: string, options?: {removeMissingValues?: boolean; onFinally?: () => void}) => {
        DEBUG && console.log('ManageData: addCategoryAndApply() [editing, value, options]', [editing, value, options])
        if (!editing) return
        if (data.fields()[editing.columnIndex].availableValues.includes(value))
            alert.error(`Value '${value}' cannot be duplicated.`)

        const onValuesUpdated = (updatedFields: Field[]) => {
            refreshDataEditor([editing.item[0]])
            refreshCellMenu(updatedFields[0], {
                hide: !data.fields()[editing.columnIndex].subTypes.includes('multipleValues'),
                newValues: editing.originalValue?.values ? [...editing.originalValue.values, value] : [value],
            })
        }

        const fields = data.fields()
        data.updateFields(
            [
                {
                    ...data.fields()[editing.columnIndex],
                    availableValues: [...fields[editing.columnIndex].availableValues, value],
                },
            ],
            {
                onComplete: (updatedFields) => {
                    data.updateValues(
                        [
                            {
                                index: editing.rowIndex!,
                                cellValues: [
                                    {
                                        columnIndex: editing.columnIndex,
                                        rowIndex: editing.rowIndex!,
                                        values: [value],
                                    },
                                ],
                            },
                        ],
                        {
                            removeMissingValues: options?.removeMissingValues,
                            onValuesUpdated: () => onValuesUpdated(updatedFields),
                            onComplete: options?.onFinally,
                            onServerError: options?.onFinally,
                        },
                    )
                    options?.onFinally?.()
                },
                onServerError: options?.onFinally,
                callerPath: ['addCategoryAndApply'],
            },
        )
    }

    const renameCategory = (newValue: string, oldValue: string) => {
        DEBUG &&
            console.log('ManageData: renameCategory() [editing, newValue, oldValue]', [editing, newValue, oldValue])
        if (!editing) return

        const affectedProfiles = data
            .profiles()
            .filter((profile) =>
                profile.cellValues.some(
                    (cellValue) => cellValue.columnIndex === editing.columnIndex && cellValue.values.includes(oldValue),
                ),
            )

        const draftAvailableValues = uniq(
            data.fields()[editing.columnIndex].availableValues.map((value) => (value === oldValue ? newValue : value)),
        )

        const onFieldsUpdated = (updatedFields: Field[]) => {
            refreshDataEditor(
                [editing.item[0]],
                affectedProfiles.map((profile) => profile.index),
            )
            refreshCellMenu(updatedFields[0], {
                hide: !data.fields()[editing.columnIndex].subTypes.includes('multipleValues'),
                newValues: editing.originalValue?.values?.map((v) => (v === oldValue ? newValue : v)),
            })
        }

        data.updateFields(
            [
                {
                    ...data.fields()[editing.columnIndex],
                    availableValues: data.fields()[editing.columnIndex].subTypes?.includes('orderCustom')
                        ? draftAvailableValues
                        : draftAvailableValues.sort(),
                },
            ],
            {
                skipValues: true,
                onComplete: (updatedFields) => {
                    data.updateValues(
                        extractColumn(affectedProfiles, editing.columnIndex).map((profile) => ({
                            ...profile,
                            cellValues: profile.cellValues.map((cellValue) => ({
                                ...cellValue,
                                values: [...cellValue.values, newValue],
                            })),
                        })),
                        {
                            onComplete: () => {
                                onFieldsUpdated(updatedFields)
                            },
                            callerPath: ['renameCategory/onComplete'],
                        },
                    )
                },
                callerPath: ['renameCategory'],
            },
        )
    }

    const applyCategoryValues = (
        values: string[],
        options?: {removeMissingValues?: boolean; onFinally?: () => void; onValuesUpdated?: () => void},
    ) => {
        DEBUG && console.log('ManageData: applyCategoryValues() [values, options]', [values, options])
        if (editing?.rowIndex === undefined) return

        const refreshColumn = () => {
            refreshCellMenu(data.fields()[editing.columnIndex], {
                hide: !data.fields()[editing.columnIndex].subTypes.includes('multipleValues'),
                newValues:
                    options?.removeMissingValues || !editing.originalValue?.values
                        ? values
                        : uniq([...editing.originalValue?.values, ...values]),
            })
        }
        const refreshCell = () => {
            refreshDataEditor([editing.columnIndex], [editing.rowIndex!])
        }

        const onValuesUpdated = () => {
            refreshColumn()
            refreshCell()
            options?.onValuesUpdated?.()
        }

        data.updateValues(
            [
                {
                    index: editing.rowIndex,
                    cellValues: [
                        {
                            rowIndex: editing.rowIndex,
                            columnIndex: editing.columnIndex,
                            values,
                        },
                    ],
                },
            ],
            {
                removeMissingValues: options?.removeMissingValues !== false,
                onValuesUpdated,
                onComplete: options?.onFinally,
                onServerError: options?.onFinally,
                callerPath: ['applyCategoryValues'],
            },
        )
    }

    const handleDropDownEnterPress = (options?: {removeMissingValues?: boolean}) => {
        DEBUG &&
            console.log('ManageData: handleDropDownEnterPress() [editing, cellEditorValue, options]', [
                editing,
                cellEditorValue,
                options,
            ])

        if (editing?.columnIndex === undefined) {
            return
        }

        const cleanup = () => {
            setMutating(undefined)
            dataEditorRef?.current?.focus()
        }

        const field = data.fields()[editing.columnIndex]
        const availableValues =
            field.name === 'Reports To'
                ? orderBy(
                      data
                          .calculateNames(data.fields(), data.profiles())
                          .map((name, index) => ({value: name, valueIdStr: data.profileIds()[index]}))
                          .filter((_, index) => index !== editing?.rowIndex),
                      (value) => value.value,
                  )
                : field.availableValues.map((value) => ({value, valueIdStr: value}))
        const availableValuesFiltered = availableValues.filter(
            (value) =>
                !cellEditorValue ||
                cellEditorValue.split(' ').every((part) => value.value.toLowerCase().includes(part.toLowerCase())),
        )

        if (field.name === 'Reports To') {
            const value = availableValuesFiltered[cellEditorHighlight % availableValuesFiltered.length].valueIdStr
            setMutating({mutation: `pick-value-${value}`})
            setData(
                editing.item,
                {data: value},
                {
                    onValuesUpdated: () => {
                        if (editing) {
                            setEditing(undefined)
                            closeCellMenu()
                            setTimeout(() => setHighlightGridSelection(true), 50)
                        }
                        cleanup()
                    },
                },
            )
            return
        }

        if (cellEditorHighlight >= 0 && availableValuesFiltered.length > 0) {
            const value = availableValuesFiltered[cellEditorHighlight % availableValuesFiltered.length].value
            setMutating({mutation: `pick-value-${value}`})
            if (options?.removeMissingValues) {
                applyCategoryValues([value], {...options, onValuesUpdated: cleanup})
            } else {
                const values = data.profiles()[editing.rowIndex!].cellValues[editing.columnIndex].values || []
                if (values.includes(value)) {
                    applyCategoryValues(
                        values.filter((_value) => _value !== value),
                        {removeMissingValues: true, onValuesUpdated: cleanup},
                    )
                } else {
                    applyCategoryValues([value], {...options, onValuesUpdated: cleanup})
                }
            }
        } else {
            setMutating({mutation: 'add-value', col: (editing?.item || [])[0], row: (editing?.item || [])[1]})
            addCategoryAndApply(cellEditorValue, {
                ...options,
                onFinally: () => {
                    setCellEditorValue('')
                    cleanup()
                },
            })
        }
    }

    const addColumn = () => {
        DEBUG && console.log('ManageData: addColumn()')
        setMutating({mutation: 'add-column'})
        data.addFields(
            [
                {
                    index: data.fields().length,
                    name: `Column ${1 + data.fields().length}`,
                    type: 'text',
                    subTypes: [],
                    availableValues: [],
                },
            ],
            {
                onComplete: () => {
                    setTimeout(
                        () => dataEditorRef.current?.scrollTo({amount: 100000, unit: 'px'}, {amount: 0, unit: 'px'}),
                        20,
                    )
                    setMutating(undefined)
                    dataEditorRef?.current?.focus()
                },
                onServerError: () => {
                    setMutating(undefined)
                    dataEditorRef?.current?.focus()
                },
                callerPath: ['addColumn'],
            },
        )
    }

    const addRow = (options?: {onValuesUpdated?: () => void}) => {
        DEBUG && console.log('ManageData: addRow()')
        setMutating({mutation: 'add-row'})
        data.addNewProfiles(
            [
                {
                    index: data.profiles().length,
                    cellValues: data.fields().map((field, columnIndex) => ({
                        columnIndex,
                        rowIndex: data.profiles().length,
                        values: field.type === 'category' || field.type === 'date' ? [] : [''],
                    })),
                },
            ],
            {
                onValuesUpdated: createOneTimeRunnable(() => {
                    DEBUG && console.log('ManageData: addRow() onValuesUpdated first')
                    setMutating(undefined)
                    setTimeout(
                        () => dataEditorRef.current?.scrollTo({amount: 0, unit: 'px'}, {amount: 100000, unit: 'px'}),
                        20,
                    )
                    dataEditorRef?.current?.focus()
                    options?.onValuesUpdated?.()
                }),
                callerPath: ['addRow'],
            },
        )
    }

    const deleteRows = (zeroBasedRowIndexes: number[]) => {
        DEBUG && console.log('ManageData: deleteRows() [zeroBasedRowIndexes]', [zeroBasedRowIndexes])
        setMutating({mutation: 'delete-rows', col: 0, row: zeroBasedRowIndexes[0]})
        data.deleteProfiles(zeroBasedRowIndexes, {
            onComplete: () => {
                setGridSelection(undefined)
                setMutating(undefined)
                dataEditorRef?.current?.focus()
            },
            onServerError: () => {
                setMutating(undefined)
                dataEditorRef?.current?.focus()
            },
            callerPath: ['deleteRows'],
        })
    }

    const deleteColumns = (zeroBasedColumnIndexes: number[]) => {
        setMutating({mutation: 'delete-columns', col: zeroBasedColumnIndexes[0], row: 0})
        data.updateFields(
            data.fields().filter((field, index) => !zeroBasedColumnIndexes.includes(index)),
            {
                removeMissingFields: true,
                onFieldsChanged: () => {
                    setGridSelection(undefined)
                    setMutating(undefined)
                    dataEditorRef?.current?.focus()
                },
                callerPath: ['deleteColumns'],
            },
        )
    }

    const handleColumnMoved = (startIndex: number, endIndex: number) => {
        setMutating({mutation: 'move-column', col: endIndex, row: 0})
        const fields = data.fields()
        const newFields = [
            ...fields.slice(0, Math.min(startIndex, endIndex)),
            ...(startIndex < endIndex
                ? fields.slice(startIndex + 1, endIndex + 1)
                : fields.slice(startIndex, startIndex + 1)),
            ...(startIndex < endIndex ? fields.slice(startIndex, startIndex + 1) : fields.slice(endIndex, startIndex)),
            ...fields.slice(Math.max(startIndex + 1, endIndex + 1)),
        ]
        DEBUG && console.log('ManageData: handleColumnMoved() [newFields]', [newFields])
        data.updateFields(newFields, {
            removeMissingFields: true,
            onFieldsChanged: () => {
                setGridSelection(undefined)
                setMutating(undefined)
                dataEditorRef?.current?.focus()
            },
            callerPath: ['handleColumnMoved'],
        })
    }

    const deleteValues = (zeroBasedColumnIndex: number, zeroBasedRowIndex: number, width: number, height: number) => {
        DEBUG &&
            console.log(
                'ManageData: deleteValues() zeroBasedColumnIndex',
                zeroBasedColumnIndex,
                'zeroBasedRowIndex',
                zeroBasedRowIndex,
                'width',
                width,
                'height',
                height,
            )
        setMutating({mutation: 'delete-values', col: zeroBasedColumnIndex, row: zeroBasedRowIndex})
        const profiles = [...new Array(height)]
            .map((_, index) => data.profiles()[zeroBasedRowIndex + index])
            .filter((profile) => profile)
        const fields = [...new Array(width)]
            .map((_, index) => data.fields()[zeroBasedColumnIndex + index])
            .filter((field) => field)
        data.updateValues(
            profiles.map((profile) => ({
                ...profile,
                cellValues: fields.flatMap((field) => [
                    {
                        columnIndex: field.index,
                        rowIndex: profile.index,
                        values: [],
                    },
                ]),
            })),
            {
                removeMissingValues: true,
                onValuesUpdated: () => {
                    setMutating(undefined)
                },
                callerPath: ['deleteValues'],
            },
        )
    }

    const handleDelete = (selection: GridSelection) => {
        if (selection.rows?.toArray()?.length) {
            deleteRows(selection.rows.toArray())
        } else if (selection.columns?.toArray()?.length) {
            deleteColumns(selection.columns.toArray())
        } else if (selection.current?.range) {
            deleteValues(
                selection.current.range.x,
                selection.current.range.y,
                selection.current.range.width,
                selection.current.range.height,
            )
        }
        return true
    }

    const pasteValues = async (zeroBasedColumnIndex: number, zeroBasedRowIndex: number, values: string[][][]) => {
        if (values.length === 0) {
            return
        }
        setMutating({mutation: 'paste-values', col: zeroBasedColumnIndex, row: zeroBasedRowIndex})
        try {
            const valuesByColumn = !values.length
                ? []
                : values[0].map((_, colIndex) =>
                      data.fields()[zeroBasedColumnIndex + colIndex]?.type === 'category'
                          ? values.map((row) =>
                                row[colIndex].flatMap((value) => value.split(',').map((value) => value.trim())),
                            )
                          : data.fields()[zeroBasedColumnIndex + colIndex]?.type === 'date'
                          ? convertDatesToYYYYMMDD(values.flatMap((row) => row[colIndex])).map((date) => [date])
                          : values.map((row) => row[colIndex]),
                  )
            const valuesByRow = !valuesByColumn.length
                ? []
                : valuesByColumn[0].map((_, rowIndex) => valuesByColumn.map((column) => column[rowIndex]))

            data.pasteValues(zeroBasedRowIndex, zeroBasedColumnIndex, valuesByRow, {
                onComplete: () => {
                    refreshDataEditor(
                        values[0].map((_, index) => zeroBasedColumnIndex + index),
                        values.map((_, index) => zeroBasedRowIndex + index),
                    )
                },
            })
        } finally {
            setMutating(undefined)
        }
    }

    const handlePaste = ([col, row]: Item, values: readonly (readonly string[])[]) => {
        navigator.clipboard.readText().then((text) => {
            if (!text) {
                pasteValues(
                    col,
                    row,
                    values.map((row) => row.map((cell) => [cell])),
                )
                return
            }
            const matrix: string[][][] = [[]]
            const pushValue = (value: string) => {
                const unquoted =
                    value.startsWith('"') && value.endsWith('"') ? value.slice(1, -1).replace(/""/g, '"') : value
                matrix[matrix.length - 1].push([unquoted])
            }
            let index = 0
            let inQuote = false
            let accumulatedValue = ''
            while (index < text.length) {
                const char = text[index]
                if (char === '"') {
                    inQuote = !inQuote
                    accumulatedValue += char
                } else if (!inQuote && (char === '\t' || char === '\n')) {
                    pushValue(accumulatedValue)
                    if (char === '\n') {
                        matrix.push([])
                    }
                    accumulatedValue = ''
                } else {
                    accumulatedValue += char
                }
                index++
            }
            pushValue(accumulatedValue)
            pasteValues(col, row, matrix)
        })
        return false
    }

    const handleGridMouseMove = (ev: GridMouseEventArgs) => {
        if (mouseIsDown) {
            setHover({row: -1, col: -1})
            return
        }
        const newColumn =
            ev.kind === 'out-of-bounds' || ev.location[0] >= (columns || []).length - 1 ? -1 : ev.location[0]
        const newRow =
            ev.kind === 'out-of-bounds' || ev.location[1] > (data.profiles() || []).length - 1 ? -1 : ev.location[1]
        if (hover.row !== newRow || hover.col !== newColumn) {
            setHover({row: newRow, col: newColumn})
        }
    }

    const updatePhotoRow = (x: number, y: number) => {
        if (x === 0) {
            setActivePhotoRowOverride(y)
            setTimeout(() => {
                photoUploadRef.current?.focus()
            }, 50)
        } else {
            setActivePhotoRowOverride(-1)
            setTimeout(() => {
                setHighlightGridSelection(true)
                dataEditorRef?.current?.focus()
            }, 0)
        }
    }

    const advanceGridSelection = (
        currentSelection: GridSelection,
        options?: {
            undoRowIncrement?: (field: Field) => boolean
            undoColumnIncrement?: () => boolean
            direction?: 'forward' | 'backward' | 'cap' | 'up' | 'right' | 'down' | 'left'
            createNewRowIfNecessary?: boolean
        },
    ) => {
        const undoRow = options?.undoRowIncrement?.(data.fields()[currentSelection?.current?.cell?.[0] ?? 0]) ? -1 : 0
        const undoColumn = options?.undoColumnIncrement?.() ? -1 : 0

        const selectedColumn = (currentSelection?.current?.cell?.[0] ?? 0) + undoColumn
        const selectedRow = (currentSelection?.current?.cell?.[1] ?? 1) + undoRow
        const atFirstRow = selectedRow === 0
        const atFirstColumn = selectedColumn === 0
        const atOrBeyondLastRow = selectedRow >= data.profiles()!.length
        const atOrBeyond2ndLastColumn = selectedColumn >= (columns?.length ?? 0) - 2
        const atOrBeyondLastColumn = selectedColumn >= (columns?.length ?? 0) - 1
        const [x, y] =
            options?.direction === 'up'
                ? [selectedColumn, atFirstRow ? selectedRow : selectedRow - 1]
                : options?.direction === 'left'
                ? [atFirstColumn ? selectedColumn : selectedColumn - 1, selectedRow]
                : options?.direction === 'down'
                ? [selectedColumn, atOrBeyondLastRow ? selectedRow : selectedRow + 1]
                : options?.direction === 'right'
                ? [atOrBeyondLastColumn ? selectedColumn : selectedColumn + 1, selectedRow]
                : options?.direction === 'cap'
                ? [
                      atOrBeyondLastColumn ? selectedColumn - 1 : selectedColumn,
                      atOrBeyondLastRow ? selectedRow - 1 : selectedRow,
                  ]
                : options?.direction === 'backward' && !atFirstRow && atFirstColumn
                ? [(columns?.length ?? 0) - 2, selectedRow - 1]
                : options?.direction === 'backward'
                ? [selectedColumn, selectedRow]
                : atOrBeyond2ndLastColumn &&
                  (selectedRow < data.profiles()!.length - 1 || options?.createNewRowIfNecessary)
                ? [0, selectedRow + 1]
                : !atOrBeyond2ndLastColumn
                ? [selectedColumn + 1, selectedRow]
                : [selectedColumn, selectedRow]
        // console.log('advanceGridSelection [x, y, undoRow, undoColumn, selectedColumn, selectedRow, atOrBeyondLastRow, atOrBeyondLastColumn, atOrBeyond2ndLastColumn, options]', [
        //     x, y, undoRow, undoColumn, selectedColumn, selectedRow, atOrBeyondLastRow, atOrBeyondLastColumn, atOrBeyond2ndLastColumn, options
        // ])
        setGridSelection({
            ...currentSelection,
            current: {
                cell: [x, y],
                range: {height: 1, width: 1, x, y},
                rangeStack: [],
            },
        })
        if (y >= data.profiles()!.length && options?.createNewRowIfNecessary) {
            addRow({
                onValuesUpdated: () => updatePhotoRow(x, y),
            })
        } else {
            updatePhotoRow(x, y)
        }
    }

    const selectionIsCategory = () =>
        gridSelection?.current?.cell?.[0] !== undefined &&
        data.fields()[gridSelection?.current?.cell?.[0]].type === 'category'

    const gridNavigationKeys = [
        'Enter',
        'Tab',
        'Escape',
        'ArrowLeft',
        'ArrowRight',
        'ArrowUp',
        'ArrowDown',
        'Shift',
        'Control',
        'Alt',
        'Delete',
    ]
    const handleGridKeyDown = (ev: {key: string; shiftKey: boolean; ctrlKey: boolean}) => {
        // console.log('handleGridKeyDown', ev)
        if (!gridNavigationKeys.includes(ev.key)) {
            if (selectionIsCategory()) {
                displayCellMenu(gridSelection!.current!.cell)
                setTimeout(() => {
                    setCellEditorValue(ev.key)
                }, 100)
            } else {
                setTimeout(() => setHighlightGridSelection(false), 0)
            }
        }
        if (ev.key === 'Tab' && ev.shiftKey && gridSelection) {
            advanceGridSelection(gridSelection, {direction: 'backward'})
        }
    }
    const handleGridKeyUp = (ev: {key: string; shiftKey: boolean; ctrlKey: boolean}) => {
        // console.log('handleGridKeyUp', ev)
        if (ev.key === 'x' && ev.ctrlKey) {
            dataEditorRef.current?.emit('copy')
            dataEditorRef.current?.emit('delete')
        } else if (ev.key === 'Enter' && gridSelection) {
            advanceGridSelection(gridSelection, {
                undoRowIncrement: (field) => !['category', 'photo'].includes(field.type) && field.name !== 'Reports To',
                createNewRowIfNecessary: true,
            })
        } else if (ev.key === 'Tab' && !ev.shiftKey && gridSelection) {
            advanceGridSelection(gridSelection, {undoColumnIncrement: () => true})
        } else if (ev.key === 'ArrowRight' && gridSelection) {
            advanceGridSelection(gridSelection, {direction: 'cap'})
        } else if (ev.key === 'ArrowDown' && gridSelection) {
            advanceGridSelection(gridSelection, {direction: 'cap'})
        } else if (ev.key === 'ArrowLeft' && gridSelection) {
            advanceGridSelection(gridSelection, {direction: 'cap'})
        } else if (ev.key === 'ArrowUp' && gridSelection) {
            advanceGridSelection(gridSelection, {direction: 'cap'})
        } else if (ev.key === 'Tab' && ev.shiftKey && gridSelection) {
            advanceGridSelection(gridSelection, {direction: 'cap'})
        } else if (ev.key === 'Escape' && gridSelection) {
            updatePhotoRow(-1, -1)
        }
        if (gridNavigationKeys.includes(ev.key)) {
            setTimeout(() => setHighlightGridSelection(true), 0)
        }
    }

    const handleGridCellClicked = (item: Item, ev: CellClickedEventArgs) => {
        setTimeout(() => setHighlightGridSelection(true), 0)
    }

    const handleDoubleClick = (ev: React.MouseEvent) => {
        if (lastColumnResize.current && lastColumnResize.current.valueOf() > new Date().valueOf() - 1000) return
        for (let index = 0; index < data.fields().length; index++) {
            const bounds = dataEditorRef.current?.getBounds(index)
            if (!bounds) return
            if (bounds.y > ev.clientY) return
            if (bounds.y + bounds.height < ev.clientY) return
            if (bounds.x + 8 > ev.clientX) return
            if (bounds.x + bounds.width - 45 < ev.clientX) continue
            displayHeaderRename(index)
        }
    }

    const handleMouseDown = (ev: React.MouseEvent) => {
        setMouseIsDown(true)
    }

    const handleMouseUp = (ev: React.MouseEvent) => {
        setMouseIsDown(false)
    }

    const getData = ([col, row]: Item): EditableGridCell => {
        if (row >= data.profiles().length || col >= data.fields().length) {
            return {
                kind: GridCellKind.Custom,
                data: {
                    kind: 'button-cell',
                    title: '',
                    color: '#000000',
                },
                readonly: true,
                allowOverlay: false,
                copyData: '',
                themeOverride: {
                    baseFontStyle: '500 25px halyard-display',
                    bgCell: '#f8f8f8',
                    borderColor: '#f8f8f8',
                    accentColor: '#f8f8f8',
                    accentFg: '#f8f8f8',
                    accentLight: '#f8f8f8',
                },
            }
        }
        const field = data.fields()[col]
        const profiles = data.profiles()
        const values = profiles[row].cellValues
            .filter((cellValue) => cellValue.columnIndex === field.index)
            .flatMap((cellValue) =>
                cellValue?.cellState?.displayValue
                    ? [cellValue?.cellState.displayValue]
                    : cellValue.values.filter((value) => value),
            )
        const invalid =
            data.profiles()[row].cellValues[col]?.cellState?.invalid &&
            !(editing?.item[0] === col && editing?.item[1] === row)
        const warning =
            data.profiles()[row].cellValues[col]?.cellState?.warning &&
            !(editing?.item[0] === col && editing?.item[1] === row)
        const themeOverride = invalid
            ? {
                  bgCell: '#ffe2e2',
                  accentLight: '#ebd5e2',
              }
            : warning
            ? {
                  bgCell: '#ffffe2',
                  accentLight: '#ebebe2',
              }
            : undefined
        if (field.type === 'category') {
            const orderedValues = field.availableValues.filter((v) => values.includes(v))
            const cell: EditableGridCell = {
                kind: GridCellKind.Text,
                data: orderedValues.join(', '),
                allowOverlay: false,
                displayData: orderedValues.join(', '),
                themeOverride,
            }
            return cell
        }
        if (field.type === 'date' || field.name === 'Reports To') {
            return {
                kind: GridCellKind.Text,
                data: values[0] || '', // Assuming date fields have a single value
                allowOverlay: false,
                displayData: values[0] || '',
                themeOverride,
            }
        }
        if (field.type === 'photo') {
            return {
                kind: GridCellKind.Image,
                data: (values || []).slice(2),
                allowOverlay: false,
                displayData: (values || []).slice(2),
                themeOverride,
                allowAdd: true,
            }
        }
        return {
            kind: GridCellKind.Text,
            data: values.join(', '),
            allowOverlay: true,
            displayData: values.join(', '),
            themeOverride,
        }
    }

    const setData = ([col, row]: Item, newValue: any, options?: {onValuesUpdated?: () => void}) => {
        if (gridSelection?.rows?.toArray()?.length) return
        if (gridSelection?.columns?.toArray()?.length) return
        DEBUG && console.log('ManageData: setData() [col, row, newValue]', [col, row, newValue])
        const value = (newValue?.data?.value?.value || newValue?.data?.value || newValue?.data) as string
        const valueIdStr = (newValue?.data?.value?.valueIdStr || newValue?.data?.valueIdStr) as string
        data.updateValues(
            [
                {
                    index: row,
                    cellValues: [
                        {
                            columnIndex: col,
                            rowIndex: row,
                            values: valueIdStr ? [valueIdStr] : value ? [value] : [],
                            cellState: {displayValue: value},
                        },
                    ],
                },
            ],
            {
                removeMissingValues: !data.fields()[col].subTypes.includes('multipleValues'),
                onValuesUpdated: () => {
                    dataEditorRef.current?.updateCells([{cell: [col, row]}])
                    options?.onValuesUpdated?.()
                },
                callerPath: ['setData'],
            },
        )
    }

    useEffect(() => {
        DEBUG &&
            console.log('ManageData: useEffect() initializeCellEditor [columns, editing, mutating]', [
                columns,
                editing,
                mutating,
            ])
        if (mutating?.col !== undefined) {
            initializeCellEditor(mutating.col, mutating.row, undefined, data.fields()[mutating.col])
        } else if (editing && editing.item[1] >= 0) {
            initializeCellEditor(editing.item[0], editing.item[1], undefined, data.fields()[editing.item[0]])
        } else if (editing) {
            initializeCellEditor(
                editing.item[0],
                undefined,
                editing.originalValue?.values?.join('\n'),
                data.fields()[editing.item[0]],
            )
        } else {
            initializeCellEditor(-1, -1, '')
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [columns, editing, mutating])

    useEffect(() => {
        DEBUG && console.log('ManageData: useEffect() calculateColumns [data.fields]', data.fields())
        setColumns(calculateColumns(data.fields()))
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [data.fields()])

    useEffect(() => {
        if (photoCropping.selection.length) {
            setModal('crop-photo')
        }
    }, [photoCropping, setModal])

    if (refreshing?.error) {
        return (
            <>
                <Typography.Heading>Something went wrong</Typography.Heading>
                <Spacer height={16} />
                <Typography.Subheading>
                    {refreshing?.error && getGraphQLErrorMessage(refreshing?.error)}
                </Typography.Subheading>
                {/* <Spacer height={32} />
                <Button variant="primary" onClick={() => setRefreshing({})}>
                    Try again
                </Button> */}
            </>
        )
    }

    if (refreshing?.loading || data.loading().grid) {
        return <PageLoading />
    }

    const gridDesiredSize = {
        width: 34 + columns!.reduce((p, c) => p + c.width, 0),
        height: 71 + data.profiles()!.length * 34,
    }
    const gridSize = {
        width: Math.min(
            width - 279,
            gridDesiredSize.height > height - 2 ? gridDesiredSize.width + 14 : gridDesiredSize.width,
        ),
        height: Math.min(
            height - 32,
            gridDesiredSize.width > width - 279 ? gridDesiredSize.height + 14 : gridDesiredSize.height,
        ),
    }
    const isEditingDate =
        editing?.columnIndex !== undefined &&
        editing?.rowIndex !== undefined &&
        data.fields()[editing.columnIndex]?.type === 'date'

    return (
        <>
            <ModalNavigator modal={modal || null} setModal={setModal}>
                <PhotoCroppingModal
                    key={`crop-photo`}
                    photoCropping={photoCropping}
                    setPhotoCropping={setPhotoCropping}
                    setModal={setModal}
                />
            </ModalNavigator>
            <div style={{maxWidth: width, display: 'flex', flexDirection: 'column', alignItems: 'center'}}>
                <div style={{width: width - 300}}>
                    <Row>
                        <Column>
                            <Typography.Heading>Your data</Typography.Heading>
                            <Row height="16px" />
                            <Typography.Subheading style={{maxHeight: '36px'}}>
                                This is the current data available for display in your Names & Faces index.
                            </Typography.Subheading>
                        </Column>
                        {currentOrganisation?.appFeatures?.communityBuild ? (
                            <Column shrink={0} centerVertical>
                                <Row height="40px"></Row>
                                <Button
                                    onClick={() => {
                                        navigator.clipboard.writeText(
                                            `${window.location.origin}/community_build/${currentOrganisation?.forms[0]?.idStr}`,
                                        )
                                        alert.success('Invite link copied.')
                                    }}
                                >
                                    Copy invite link
                                </Button>
                            </Column>
                        ) : (
                            <></>
                        )}
                    </Row>
                    <Row height="40px"></Row>
                </div>
            </div>
            <div style={{maxWidth: width, display: 'flex', flexDirection: 'column', alignItems: 'flex-start'}}>
                <div
                    style={{
                        width: gridSize.width,
                        border: 'solid 1px #e8e8e8',
                        borderRight: 'none',
                        display: 'inline-block',
                        marginLeft: '30px',
                    }}
                    onMouseMove={setOtherMouseGesture}
                    onWheel={setOtherMouseGesture}
                    onDoubleClick={handleDoubleClick}
                    onMouseDown={handleMouseDown}
                    onMouseUp={handleMouseUp}
                >
                    <DataEditor
                        width={gridSize.width}
                        height={gridSize.height}
                        getCellContent={getData}
                        columns={columns!}
                        rows={data.profiles()!.length + 1}
                        ref={dataEditorRef}
                        customRenderers={customRenderers}
                        theme={{
                            fontFamily: 'halyard-display',
                            accentColor: highlightGridSelection ? colors.primary['100'] : '#ededf3',
                            accentFg: '#FFFFFF',
                            accentLight: highlightGridSelection ? colors.primary['10'] : 'white',
                            headerFontStyle: '600 14px',
                            baseFontStyle: '14px',
                            editorFontSize: '14px',
                            bgCellMedium: '#f8f8f8',
                        }}
                        rightElement={false}
                        maxColumnWidth={1000}
                        minColumnWidth={10}
                        smoothScrollX={true}
                        smoothScrollY={true}
                        rowMarkers={'both'}
                        rowSelectionMode="multi"
                        gridSelection={gridSelection}
                        getCellsForSelection={true}
                        onCellEdited={setData}
                        onCellActivated={displayCellMenu}
                        onColumnResize={onColumnResize}
                        onColumnMoved={handleColumnMoved}
                        onDelete={handleDelete}
                        onPaste={handlePaste}
                        onGridSelectionChange={(gridSelection) => setGridSelection(gridSelection)}
                        onKeyDown={handleGridKeyDown}
                        onKeyUp={handleGridKeyUp}
                        onMouseMove={handleGridMouseMove}
                        onCellClicked={handleGridCellClicked}
                        drawFocusRing={highlightGridSelection}
                    ></DataEditor>
                    <AddRowButton
                        onClick={() => addRow()}
                        className={`${mutating?.mutation || ''} ${
                            gridSize.width < gridDesiredSize.width ? 'scrolling-horizontally' : ''
                        }`}
                    >
                        {mutating?.mutation !== 'add-row' ? '+' : <Spinner></Spinner>}
                    </AddRowButton>
                    {dataEditorRef.current && (
                        <ManagePhoto
                            fileInputRef={photoUploadRef}
                            hoverColumn={hover.col}
                            hoverRow={hover.row}
                            profiles={data.profiles()}
                            profileIdStrs={data.profileIds()}
                            otherMouseGesture={otherMouseGesture}
                            activeRowOverride={activePhotoRowOverride}
                            getCellBounds={dataEditorRef.current!.getBounds}
                            organisationIdStr={currentOrganisation!.idStr}
                            photoCropping={photoCropping}
                            setPhotoCropping={setPhotoCropping}
                            onKeyDown={(ev) => {
                                if (ev.key === 'Tab' && !ev.shiftKey && gridSelection) {
                                    advanceGridSelection(gridSelection, {direction: 'right'})
                                } else if (ev.key === 'ArrowRight' && gridSelection) {
                                    advanceGridSelection(gridSelection, {direction: 'right'})
                                } else if (ev.key === 'ArrowDown' && gridSelection) {
                                    advanceGridSelection(gridSelection, {direction: 'down'})
                                } else if (ev.key === 'ArrowLeft' && gridSelection) {
                                    advanceGridSelection(gridSelection, {direction: 'left'})
                                } else if (ev.key === 'ArrowUp' && gridSelection) {
                                    advanceGridSelection(gridSelection, {direction: 'up'})
                                }
                                handleGridKeyDown(ev)
                            }}
                            onKeyUp={handleGridKeyUp}
                        ></ManagePhoto>
                    )}
                    {dataEditorRef.current && (
                        <ManageColumn
                            hoverColumn={hover.col}
                            hoverRow={hover.row}
                            otherMouseGesture={otherMouseGesture}
                            columns={data.fields().map((column, index) => ({
                                ...column,
                                isSelected: gridSelection?.columns?.hasIndex(index) || false,
                                subTypes: column.subTypes,
                            }))}
                            getCellBounds={(col, row) => {
                                const bounds = dataEditorRef.current!.getBounds(col, row)
                                if (!bounds) return bounds
                                const adjustedWidth = bounds.width - Math.max(0, bounds.x + bounds.width - width + 28)
                                if (adjustedWidth < 68) return undefined
                                return {...bounds, width: adjustedWidth}
                            }}
                            updateField={(
                                col: number,
                                field: Partial<Field>,
                                valueMutator?: (values: string[]) => (string | undefined)[],
                            ) => {
                                DEBUG &&
                                    console.log(
                                        'ManageData/ManageColumn updateField() col',
                                        col,
                                        'field',
                                        field,
                                        'valueMutator',
                                        valueMutator,
                                    )
                                if (data.fields()[col].name !== 'Reports To' && field.name === 'Reports To') {
                                    data.updateFields([{...data.fields()[col], ...field}], {
                                        skipValues: true,
                                        onFieldsChanged: () => {
                                            data.updateValues(
                                                convertToReportsTo(
                                                    data.calculateNames(data.fields(), data.profiles()),
                                                    data.profileIds(),
                                                    data.profiles(),
                                                    data.fields()[col].index,
                                                ),
                                                {
                                                    removeMissingValues: true,
                                                    callerPath: ['ManageColumn/updateField/onComplete'],
                                                },
                                            )
                                        },
                                        callerPath: ['ManageColumn/updateField'],
                                    })
                                    return
                                }
                                if (data.fields()[col].name === 'Reports To' && field.name !== 'Reports To') {
                                    data.updateFields([{...data.fields()[col], ...field}], {
                                        skipValues: true,
                                        onFieldsChanged: () => {
                                            data.updateValues(
                                                convertFromReportsTo(data.profiles(), data.fields()[col].index),
                                                {
                                                    removeMissingValues: true,
                                                    callerPath: ['ManageColumn/updateField/onComplete'],
                                                },
                                            )
                                        },
                                        callerPath: ['ManageColumn/updateField'],
                                    })
                                    return
                                }
                                if (
                                    data.fields()[col].type !== 'category' &&
                                    field?.type === 'category' &&
                                    field?.availableValues?.length === 0
                                ) {
                                    field.availableValues = uniq(
                                        data.profiles().flatMap((profile) =>
                                            profile.cellValues[col].values
                                                .flatMap((value) => value.split(','))
                                                .map((value) => value.trim())
                                                .filter((value) => value),
                                        ),
                                    )
                                }
                                if (field?.type === 'date') {
                                    data.updateFields([{...data.fields()[col], ...field}], {
                                        skipValues: true,
                                        onFieldsChanged: () => {
                                            if (valueMutator) {
                                                const updateProfiles = valueMutator(
                                                    data
                                                        .profiles()
                                                        .flatMap((profile) => profile.cellValues[col].values[0]),
                                                ).map((value, index) => ({
                                                    ...data.profiles()[index],
                                                    cellValues: [
                                                        {
                                                            ...data.profiles()[index].cellValues[col],
                                                            values: value ? [value] : [],
                                                        },
                                                    ],
                                                }))
                                                if (updateProfiles.length) {
                                                    data.updateValues(updateProfiles, {
                                                        removeMissingValues: true,
                                                        callerPath: ['ManageColumn/updateField'],
                                                    })
                                                }
                                            }
                                            // DEBUG && console.log('updateField done')
                                        },
                                        callerPath: ['ManageColumn/updateField'],
                                    })
                                } else {
                                    data.updateFields([{...data.fields()[col], ...field}], {
                                        onFieldsChanged: () => {
                                            if (valueMutator) {
                                                const updateProfiles = data.profiles().flatMap((profile) => {
                                                    const cellValue = profile.cellValues[col]
                                                    const mutation = valueMutator(cellValue.values).map(
                                                        (value, index) => ({
                                                            current: cellValue.values[index],
                                                            mutated: value,
                                                        }),
                                                    )
                                                    return mutation.some((pair) => pair.current !== pair.mutated) ||
                                                        mutation.length !== cellValue.values.length
                                                        ? [
                                                              {
                                                                  ...profile,
                                                                  cellValues: [
                                                                      {
                                                                          ...cellValue,
                                                                          values: mutation
                                                                              .map((pair) => pair.mutated)
                                                                              .filter((value) => value) as string[],
                                                                      },
                                                                  ],
                                                              },
                                                          ]
                                                        : []
                                                })
                                                if (updateProfiles.length) {
                                                    data.updateValues(updateProfiles, {
                                                        removeMissingValues: true,
                                                        callerPath: ['ManageColumn/updateField'],
                                                    })
                                                }
                                            }
                                            // DEBUG && console.log('updateField done')
                                        },
                                        callerPath: ['ManageColumn/updateField'],
                                    })
                                }
                            }}
                            sortField={data.sortField()}
                            setSortField={data.setSortField}
                            onDeleteColumn={(colIndex) => {
                                deleteColumns([colIndex])
                            }}
                        ></ManageColumn>
                    )}
                    <CellEditor id="activeCellEditor" ref={cellRef}>
                        {['delete-rows', 'delete-columns', 'move-column', 'delete-values', 'paste-values'].includes(
                            mutating?.mutation || '',
                        ) ? (
                            <CellEditorSpinnerContainer className={mutating?.mutation || ''}>
                                <Spinner></Spinner>
                            </CellEditorSpinnerContainer>
                        ) : ['rename-column'].includes(mutating?.mutation || '') ? (
                            <CellEditorSpinnerContainer className={mutating?.mutation || ''}>
                                <Spinner tint="#FFFFFF"></Spinner>
                            </CellEditorSpinnerContainer>
                        ) : (
                            <CellEditorInput
                                id="activeCellEditorInput"
                                type="text"
                                value={
                                    isEditingDate
                                        ? `${datePicker.yearValue}/${datePicker.monthValue}/${datePicker.dayValue}`
                                        : cellEditorValue
                                }
                                pattern={isEditingDate ? '[0-9]' : undefined}
                                inputMode={isEditingDate ? 'numeric' : undefined}
                                onChange={(e) => {
                                    if (!isEditingDate) {
                                        setCellEditorValue(e.target.value)
                                    }
                                }}
                                onKeyDown={
                                    isEditingDate
                                        ? datePicker.handleKeyDown
                                        : (e) => {
                                              if (e.key === 'Tab' && editing && gridSelection) {
                                                  const selectedColumn = gridSelection?.current?.cell?.[0] ?? 0
                                                  const selectedRow = gridSelection?.current?.cell?.[1] ?? 1
                                                  const [x, y] =
                                                      selectedRow < data.profiles()!.length - 1 &&
                                                      selectedColumn >= (columns?.length ?? 0) - 1
                                                          ? [0, selectedRow + 1]
                                                          : selectedRow === data.profiles()!.length - 1 &&
                                                            selectedColumn === (columns?.length ?? 0) - 1
                                                          ? [selectedColumn, selectedRow]
                                                          : [selectedColumn + 1, selectedRow]
                                                  setGridSelection({
                                                      ...gridSelection,
                                                      current: {
                                                          cell: [x, y],
                                                          range: {height: 1, width: 1, x, y},
                                                          rangeStack: [],
                                                      },
                                                  })
                                                  refreshCellMenu(data.fields()[editing.columnIndex], {hide: true})
                                                  dataEditorRef?.current?.focus()
                                                  e.preventDefault()
                                              }
                                          }
                                }
                                onKeyPress={
                                    isEditingDate
                                        ? datePicker.handleKeyPress
                                        : (e) => {
                                              if (e.key === 'Enter') {
                                                  if (!editing) return
                                                  editing?.rowIndex === undefined || editing?.rowIndex < 0
                                                      ? completeHeaderRename()
                                                      : handleDropDownEnterPress({
                                                            removeMissingValues:
                                                                !editing ||
                                                                !(
                                                                    data.fields()[editing.columnIndex]?.subTypes || []
                                                                ).includes('multipleValues'),
                                                        })
                                              }
                                          }
                                }
                                onKeyUp={(e) => {
                                    if (e.key === 'ArrowDown') {
                                        setCellEditorHighlight(cellEditorHighlight + 1)
                                    } else if (e.key === 'ArrowUp') {
                                        setCellEditorHighlight(cellEditorHighlight - 1)
                                    } else if (e.key === 'Escape' && editing) {
                                        refreshCellMenu(data.fields()[editing.columnIndex], {hide: true})
                                        dataEditorRef?.current?.focus()
                                    } else if (isEditingDate) {
                                        datePicker.handleKeyUp(e)
                                    }
                                }}
                                onFocus={() => isEditingDate && datePicker.setHasFocus(true)}
                                onBlur={() => isEditingDate && datePicker.setHasFocus(false)}
                                onMouseUp={(ev) => {
                                    if (isEditingDate) {
                                        datePicker.handleMouseUp(ev)
                                    }
                                }}
                                ref={cellEditorInputRef}
                            ></CellEditorInput>
                        )}
                    </CellEditor>
                    <Panel
                        targetRef={cellRef}
                        {...cellMenuProps}
                        onClose={() => refreshCellMenu(data.fields()[0], {hide: true})}
                    >
                        <PanelLayout key="cellMenu">
                            {editing && data.fields()[editing.columnIndex]?.name === 'Reports To' && (
                                <PickReportsTo
                                    availableValues={data
                                        .calculateNames(data.fields(), data.profiles())
                                        .map((name, index) => ({value: name, valueIdStr: data.profileIds()[index]}))
                                        .filter((_, index) => index !== editing?.rowIndex)}
                                    selectedValue={editing?.originalValue}
                                    searchValue={cellEditorValue?.split('\n')?.[0] || ''}
                                    pickValue={(value, onComplete) => {
                                        setTimeout(() => {
                                            dataEditorRef?.current?.focus()
                                        }, 50)
                                        setData(
                                            editing.item,
                                            {data: value},
                                            {
                                                onValuesUpdated: () => {
                                                    if (editing) {
                                                        setEditing(undefined)
                                                        closeCellMenu()
                                                        setTimeout(() => setHighlightGridSelection(true), 50)
                                                    }
                                                    onComplete?.()
                                                },
                                            },
                                        )
                                    }}
                                    mutating={mutating}
                                    setMutating={setMutating}
                                    cellEditorHighlight={cellEditorHighlight}
                                ></PickReportsTo>
                            )}
                            {editing && data.fields()[editing.columnIndex]?.type === 'category' && (
                                <PickCategory
                                    availableValues={
                                        editing ? data.fields()[editing.columnIndex]?.availableValues || [] : []
                                    }
                                    selectedValues={editing?.originalValue?.values || []}
                                    searchValue={cellEditorValue?.split('\n')?.[0] || ''}
                                    addValue={addCategoryAndApply}
                                    pickValue={(values, options) => {
                                        setTimeout(() => {
                                            dataEditorRef?.current?.focus()
                                        }, 50)
                                        applyCategoryValues(values, options)
                                    }}
                                    updateValue={renameCategory}
                                    mutating={mutating}
                                    setMutating={setMutating}
                                    pickMultiple={
                                        editing?.columnIndex !== undefined &&
                                        (data.fields()[editing.columnIndex]?.subTypes || []).includes('multipleValues')
                                    }
                                    cellEditorHighlight={cellEditorHighlight}
                                ></PickCategory>
                            )}
                            {editing &&
                                data.fields()[editing.columnIndex]?.type === 'date' &&
                                datePicker.inputPosition !== 'complete' && (
                                    <Calendar
                                        yearValue={datePicker.yearValue}
                                        monthValue={datePicker.monthValue}
                                        dayValue={datePicker.dayValue}
                                        setYearValue={datePicker.setYearValue}
                                        setMonthValue={datePicker.setMonthValue}
                                        setDayValue={datePicker.setDayValue}
                                        inputPosition={datePicker.inputPosition}
                                    />
                                )}
                        </PanelLayout>
                    </Panel>
                </div>
                <AddColumnButton
                    onClick={() => addColumn()}
                    className={`${mutating?.mutation || ''} ${
                        gridSize.height < gridDesiredSize.height ? 'scrolling-vertically' : ''
                    } ${gridSize.width < gridDesiredSize.width ? 'scrolling-horizontally' : ''}`}
                    style={{left: gridSize.width + 3}}
                >
                    {mutating?.mutation !== 'add-column' ? '+' : <Spinner></Spinner>}
                </AddColumnButton>
            </div>
        </>
    )
}
